/* Copyright (c) 2017 Backpack Health LLC */

/* global window, BPH_SUBDOMAIN, SHARE_URL_SUBDOMAIN_PREFIX, APP_ROOT, APP_INDEX, IS_ANDROID_BUILD */

import { localstore, sessionstore, authToken, joinGroupIntent, logoutEvent } from '../utilities/LocalStorage';
import CustomerService from './CustomerService';
import UserMetrics from './UserMetrics';
import SessionService from '../apiServices/SessionService';
import BREAKPOINTS from '../app/styles/Breakpoints';

const AppUtils = {

  logout(options, callback) {
    window.BPH = window.BPH || {};
    window.BPH.CORDOVA_LOGGED_IN = false;

    if (window.LOGOUT_STORAGE_EVENT_HANDLER) {
      window.removeEventListener('storage', window.LOGOUT_STORAGE_EVENT_HANDLER);
      window.LOGOUT_STORAGE_EVENT_HANDLER = null;
    }

    logoutEvent.set();

    if (window.NOTIFICATION_TIMER) {
      clearInterval(window.NOTIFICATION_TIMER);
      window.NOTIFICATION_TIMER = null;
    }

    if (window.REFRESH_TOKEN_TIMER) {
      clearInterval(window.REFRESH_TOKEN_TIMER);
      window.REFRESH_TOKEN_TIMER = null;
    }

    if (window.cancelIdleTimer) {
      window.cancelIdleTimer();
    }

    if (authToken.get()) {
      SessionService.logout(() => {
        AppUtils.clearSavedData(options);
        callback && callback();
      });
    } else {
      AppUtils.clearSavedData(options);
      callback && callback();
    }
  },

  clearSavedData(options = { retainMetrics: false }) {
    localstore.clear();
    sessionstore.clear();
    CustomerService.shutdown();
    if (!options.retainMetrics) {
      UserMetrics.reset();
    }
  },

  clearSavedIntent() {
    joinGroupIntent.pop();
  },

  reloadApp() {
    window.location.replace(`${APP_ROOT}${APP_INDEX}`);
  },

  reloadAppToPathname(pathname) {
    const root = APP_ROOT.substring(0, APP_ROOT.length - 1);
    window.location.replace(`${root}${pathname}`);
  },

  extractDataFromAuthToken(token) {
    const tokenParts = token.split('.');
    return JSON.parse(window.atob(tokenParts[1]));
  },

  getSubdomain() {
    return BPH_SUBDOMAIN;
  },

  getShareUrlSubdomainPrefix() {
    return SHARE_URL_SUBDOMAIN_PREFIX;
  },

  isNonIpadIosDevice() {
    return /iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
  },

  isIosDevice() {
    return (
      /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream
    );
  },

  isAndroidDevice() {
    return /Android/.test(window.navigator.userAgent) && !window.MSStream;
  },

  isMobileDevice() {
    return (
      (/iPad|iPhone|iPod|Android/.test(window.navigator.userAgent) &&
        !window.MSStream) ||
      IS_ANDROID_BUILD
    );
  },

  isSafari() {
    const isSafari =
      /constructor/i.test(window.HTMLElement) ||
      (function Safari7(p) {
        return p.toString() === '[object SafariRemoteNotification]';
      }(
        !window.safari ||
          (typeof window.safari !== 'undefined' &&
            window.safari.pushNotification)
      ));
    return isSafari;
  },

  isIE11() {
    return /Trident/.test(window.navigator.userAgent);
  },

  isNotSupportAutoCap() {
    const raw = window.navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
    const version = raw ? parseInt(raw[2], 10) : false;
    return version && version <= 44;
  },

  hasSessionToken() {
    const savedToken = authToken.get();
    return savedToken !== '';
  },

  googlePlayAppLink() {
    switch (BPH_SUBDOMAIN) {
      case 'dev-my':
        return 'https://play.google.com/store/apps/details?id=com.backpackhealth.backpackdev';
      case 'qa-my':
        return 'https://play.google.com/store/apps/details?id=com.backpackhealth.backpackqa';
      case 'my':
      default:
        return 'https://play.google.com/store/apps/details?id=com.backpackhealth.backpack';
    }
  },

  itunesAppLink() {
    return 'https://itunes.apple.com/us/app/id1142624598?ls=1&mt=8';
  },

  appLink() {
    return this.isAndroidDevice() ? this.googlePlayAppLink() : this.itunesAppLink();
  },

  cssColor(colorData) {
    return colorData ? `rgba(${colorData.r}, ${colorData.g}, ${colorData.b}, ${colorData.a})` : '';
  },

  rgbToHex(r, g, b) {
    const stringToHex = c => {
      const hex = Math.round(c).toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    };
    return `#${stringToHex(r)}${stringToHex(g)}${stringToHex(b)}`;
  },

  getQueryParams(query) {
    const queryString = query[0] === '?' ? query.slice(1, query.length) : query;
    const pairs = queryString.split('&');

    const queryParams = {};
    pairs.forEach(p => {
      const keyValue = p.split('=');
      queryParams[keyValue[0]] = keyValue[1];
    });

    return queryParams;
  },

  getStyle(el, styleName) {
    if (window.getComputedStyle) {
      return window.getComputedStyle(el, null)[styleName];
    } else if (el.currentStyle) {
      return el.currentStyle[styleName];
    }
    return null;
  },

  addFlashToElement(el, flashClassName = 'util-animated-background-flash') {
    el.classList.add(flashClassName);

    let timeout = setTimeout(() => {
      el.classList.remove(flashClassName);
      clearTimeout(timeout);
      timeout = null;
    }, 1100);
  },

  deepCompare(object1, object2, properties, sorter) {
    const obj1 = object1 || {};
    const obj2 = object2 || {};
    const differentProperties = [];

    properties.forEach(p => {
      let aData = null;
      let bData = null;

      if (obj1[p]) {
        aData = sorter ? obj1[p].sort(sorter) : obj1[p];
      }
      if (obj2[p]) {
        bData = sorter ? obj2[p].sort(sorter) : obj2[p];
      }

      if (JSON.stringify(aData) !== JSON.stringify(bData)) {
        differentProperties.push(p);
      }
    });

    return differentProperties;
  },

  getTextNodesIn(elem) {
    let textNodes = [];

    if (!elem) {
      return textNodes;
    }

    for (let i = 0; i < elem.children.length; i++) {
      const childNode = elem.children[i];
      if (!childNode.children.length) {
        textNodes.push(childNode);
      } else {
        const childNodes = AppUtils.getTextNodesIn(elem.children[i]);
        textNodes = [...textNodes, ...childNodes];
      }
    }

    return textNodes;
  },

  getElementCoords(elem) {
    const box = elem.getBoundingClientRect();

    return {
      top: box.top + window.pageYOffset,
      left: box.left + window.pageXOffset,
    };
  },

  enableTouchRelatedStyles() {
    if (IS_ANDROID_BUILD) {
      document.documentElement.classList.add('GLOBAL_CAN_TOUCH');
      return;
    }

    const addTouchClass = () => {
      document.documentElement.classList.add('GLOBAL_CAN_TOUCH');
      document.removeEventListener('touchstart', addTouchClass, false);
    }
    document.addEventListener('touchstart', addTouchClass);
  },

  isVersionMoreRecent(version = '', latestVersion = '') {
    const newVersionSplit = version.split('.');
    const latestVersionSplit = latestVersion.split('.');
    const limit = Math.min(newVersionSplit.length, latestVersionSplit.length);

    for (let i=0; i < limit; i++) {
      const newNumber = Number(newVersionSplit[i]);
      const lastNumber = Number(latestVersionSplit[i]);

      if (newNumber > lastNumber) {
        return true;
      } else if (newNumber < lastNumber) {
        return false
      }
    }

    return newVersionSplit.length > latestVersionSplit.length;
  },

  isWideView() {
    return window.innerWidth > BREAKPOINTS.md;
  },

  isTablet() {
    return window.innerWidth > BREAKPOINTS.tabletWidth;
  },

  downloadFile(uri, fileName) {
    const link = document.createElement('a');
    link.href = uri;
    if (fileName) {
      link.setAttribute('download', fileName);
    }
    link.click();

    document.removeChild(link);
  },

  getClientLocales() {
    let languages = window.navigator.languages ? window.navigator.languages : [];
    if (languages.length === 0) {
      languages = window.navigator.language ? [window.navigator.language] : [window.navigator.browserLanguage];
    }

    // default language based on marketing web referrer lang setting
    const knownReferrerDomain = 'backpackhealth.com';
    const referrerOrigin = document.referrer && typeof window.URL === 'function' ? new window.URL(document.referrer).origin : '';
    if (referrerOrigin.endsWith(knownReferrerDomain)) {
      const localeRegexp = new RegExp(`(.*${knownReferrerDomain}/)([a-z]{2})/.*`);
      const match = localeRegexp.exec(document.referrer);
      if (match && match.length > 2) {
        languages = [match[2]];
      }
    }

    const searchParams = AppUtils.getQueryParams(window.location.search);
    if (searchParams.lang) {
      languages = [searchParams.lang];
    }

    return languages;
  },

};

export default AppUtils;
